// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { getFileText } from '@/file/api/get-file-text';
import { parseSerialsFile } from '@/shipment/utilities/parse-serials-file';
import { useQuery } from '@tanstack/react-query';

async function fetchSerials(fileKey: string): Promise<string[]> {
  try {
    const text = await getFileText(fileKey);

    return parseSerialsFile(text, `${fileKey}.csv`).serials;
  } catch (error) {
    // Reported here rather than from the consumer: a query has no equivalent of
    // the mutation error handler, and a hover that quietly renders nothing
    // would hide a broken file endpoint.
    Sentry.captureException(error);
    throw error;
  }
}

/**
 * Reads the serials out of a stored serial CSV. The file is written once and
 * never rewritten, so the result is cached indefinitely and the caller controls
 * when it is fetched at all through `enabled`.
 */
export function useFileSerials(fileKey: string, enabled: boolean) {
  return useQuery({
    queryKey: ['file', fileKey, 'serials'],
    queryFn: () => fetchSerials(fileKey),
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}
